import type { KyInstance } from 'ky';

import {
    activateHip3,
    approveBuilderFee,
    cancelLimitOrder,
    cancelUntrackedOrder,
    closeTrade,
    ensureTriggers,
    executeLimitOrder,
    fetchAgentStatus,
    fetchBalance,
    fetchBalanceLight,
    fetchExposure,
    fetchPosition,
    fetchPositions,
    fetchUserTrades,
    fetchWallet,
    openLimitOrder,
    openTrade,
    quoteOpen,
    reactivateAgent,
    reportAgentPruned,
    updateMargin,
    updateTriggers,
    type AgentPrunedReason,
} from './services';
import type {
    AgentActivateHip3Response,
    AgentReactivateResponse,
    AgentStatusResponse,
    BalanceLightResponse,
    BalanceResponse,
    CancelUntrackedOrderRequest,
    CancelUntrackedOrderResponse,
    EnsureTriggersResponse,
    FetchExposureParams,
    FetchTradesParams,
    FundAuthorizationsWire,
    LimitCancelRequest,
    LimitCancelResponse,
    LimitExecuteRequest,
    LimitExecuteResponse,
    LimitOpenRequest,
    LimitOpenResponse,
    PositionResponse,
    PositionsResponse,
    QuoteResponse,
    TradeCloseRequest,
    TradeCloseResponse,
    TradeOpenRequest,
    TradeOpenResponse,
    UntrackedExposure,
    UpdateMarginRequest,
    UpdateMarginResponse,
    UpdateTriggersRequest,
    UpdateTriggersResponse,
    UserTrade,
    WalletResponse,
} from './types';

/** Which trading flow a backend serves: V1 = the custodial stack, V2 = the browser wallet. */
export type SpeedBackendKind = 'v1' | 'v2';

/**
 * `GET /speed/trades` params: the same object `features/wallet/services/tradeApi.ts`
 * `fetchUserTrades` takes.
 */
export interface SpeedUserTradesParams extends FetchTradesParams {
    /** Accepted for call-site compatibility; the backend pins /speed/trades to 999. */
    networkId?: number;
}

/** `POST /speed/agent/builder-fee` answer. */
export type AgentBuilderFeeResponse = Awaited<
    ReturnType<typeof approveBuilderFee>
>;

/**
 * Every speed-trading call the trading hooks and components make, behind one seam.
 *
 * The V1 implementation ({@link createV1Backend}) binds each method to the existing service
 * function with the client its callers have always used; the V2 implementation (the browser
 * wallet) answers the same shapes from Hyperliquid directly. Callers gate on `backend !== null`
 * exactly where they used to gate on their ky client.
 *
 * Optional `signal`s are the same ones the service functions take, so React Query keeps
 * cancelling superseded reads.
 */
export interface SpeedBackend {
    readonly kind: SpeedBackendKind;

    // --- reads ---
    readonly fetchPosition: (
        coin: string,
        signal?: AbortSignal,
    ) => Promise<PositionResponse>;
    readonly fetchPositions: (
        signal?: AbortSignal,
    ) => Promise<PositionsResponse>;
    readonly fetchBalance: (signal?: AbortSignal) => Promise<BalanceResponse>;
    readonly fetchBalanceLight: (
        signal?: AbortSignal,
    ) => Promise<BalanceLightResponse>;
    readonly fetchWallet: (signal?: AbortSignal) => Promise<WalletResponse>;
    readonly fetchAgentStatus: (
        signal?: AbortSignal,
    ) => Promise<AgentStatusResponse>;
    readonly fetchUserTrades: (
        params?: SpeedUserTradesParams,
    ) => Promise<UserTrade[]>;
    /** `null` when the read could not see the exchange (see `fetchExposure` in services). */
    readonly fetchExposure: (
        params: FetchExposureParams,
    ) => Promise<UntrackedExposure | null>;
    readonly quoteOpen: (body: TradeOpenRequest) => Promise<QuoteResponse>;

    // --- writes ---
    readonly openTrade: (body: TradeOpenRequest) => Promise<TradeOpenResponse>;
    readonly closeTrade: (
        body: TradeCloseRequest,
    ) => Promise<TradeCloseResponse>;
    readonly openLimitOrder: (
        body: LimitOpenRequest,
    ) => Promise<LimitOpenResponse>;
    readonly cancelLimitOrder: (
        body: LimitCancelRequest,
    ) => Promise<LimitCancelResponse>;
    readonly executeLimitOrder: (
        body: LimitExecuteRequest,
    ) => Promise<LimitExecuteResponse>;
    readonly updateTriggers: (
        body: UpdateTriggersRequest,
    ) => Promise<UpdateTriggersResponse>;
    readonly ensureTriggers: () => Promise<EnsureTriggersResponse>;
    readonly updateMargin: (
        body: UpdateMarginRequest,
    ) => Promise<UpdateMarginResponse>;
    readonly cancelUntrackedOrder: (
        body: CancelUntrackedOrderRequest,
    ) => Promise<CancelUntrackedOrderResponse>;
    readonly reactivateAgent: (
        fundAuthorizations?: FundAuthorizationsWire,
    ) => Promise<AgentReactivateResponse>;
    readonly approveBuilderFee: () => Promise<AgentBuilderFeeResponse>;
    readonly activateHip3: () => Promise<AgentActivateHip3Response>;
    readonly reportAgentPruned: (
        reason: AgentPrunedReason,
    ) => Promise<AgentStatusResponse>;
}

/**
 * The V1 (custodial) backend: a pure binding of the existing service functions.
 *
 * Each method calls the same function with the same client its callers passed before the seam:
 * the read-only reads served by factions.backend's speed facet (`position`, `positions`,
 * `balance`, `balance/light`, `wallet`, `trades`, `exposure`, `quote`) take `factionsApi`; the
 * agent-status read and every write take the MoonHub `authedApi`. No logic, no error mapping.
 */
export function createV1Backend(
    authedApi: KyInstance,
    factionsApi: KyInstance,
): SpeedBackend {
    return {
        kind: 'v1',

        fetchPosition: async (coin, signal) =>
            fetchPosition(factionsApi, coin, signal),
        fetchPositions: async (signal) => fetchPositions(factionsApi, signal),
        fetchBalance: async (signal) => fetchBalance(factionsApi, signal),
        fetchBalanceLight: async (signal) =>
            fetchBalanceLight(factionsApi, signal),
        fetchWallet: async (signal) => fetchWallet(factionsApi, signal),
        fetchAgentStatus: async (signal) => fetchAgentStatus(authedApi, signal),
        fetchUserTrades: async (params) => fetchUserTrades(factionsApi, params),
        fetchExposure: async (params) => fetchExposure(factionsApi, params),
        quoteOpen: async (body) => quoteOpen(factionsApi, body),

        openTrade: async (body) => openTrade(authedApi, body),
        closeTrade: async (body) => closeTrade(authedApi, body),
        openLimitOrder: async (body) => openLimitOrder(authedApi, body),
        cancelLimitOrder: async (body) => cancelLimitOrder(authedApi, body),
        executeLimitOrder: async (body) => executeLimitOrder(authedApi, body),
        updateTriggers: async (body) => updateTriggers(authedApi, body),
        ensureTriggers: async () => ensureTriggers(authedApi),
        updateMargin: async (body) => updateMargin(authedApi, body),
        cancelUntrackedOrder: async (body) =>
            cancelUntrackedOrder(authedApi, body),
        reactivateAgent: async (fundAuthorizations) =>
            reactivateAgent(authedApi, fundAuthorizations),
        approveBuilderFee: async () => approveBuilderFee(authedApi),
        activateHip3: async () => activateHip3(authedApi),
        reportAgentPruned: async (reason) =>
            reportAgentPruned(authedApi, reason),
    };
}
